import {Bar, Text, PolicyLink, Actions, ScrollClearance, CustomizeButton, AnswerButton} from "./CookieBanner.styles";
import {useOverlayActivity} from "components/useOverlayActivity";
import {useEffect, useState} from "react";
import {ABOVE_SHEET_ATTR} from "components/CardSheet/cardSheetDismiss";
import {useConsent} from "consent/useConsent";
import {consentBarReserve, consentClearance, lowestRestingBottom} from "consent/consentClearance";
import {scrollReachableControls} from "consent/scrollReach";
import {PolicyDialog} from "legal/PolicyDialog";
import {CookieSettingsDialog} from "legal/CookieSettingsDialog";
import {useMessages} from "i18n";

/**
 * Opt-in cookie consent banner (ticket ui#72), mounted app-wide in {@link RootLayout}.
 *
 * Shown only until the visitor makes a choice (`!consent.decided`). Opt-in model, and THREE purposes
 * since ui#2331 — analytics, advertising measurement and the optional email offers:
 *   - Accept all → all three granted, decided. The notice sentence says so (ui#2596).
 *   - Reject     → all three denied, decided → nothing loads and no request reaches Google, and the
 *                  email denial is a marketing objection (see `grantAllConsent`/`denyAllConsent`).
 *   - Customize  → opens the Cookie Settings dialog, where each purpose is chosen separately.
 * A link opens the Cookie Policy dialog.
 *
 * The banner records the DECISION and nothing else: applying it to Google tagging belongs to
 * `analytics/useAnalyticsConsent`, mounted above this component, because this one unmounts the moment
 * consent is decided (ui#256).
 *
 * SSG-safe: a `mounted` guard keeps the banner OUT of the pre-rendered HTML, so returning visitors
 * who already consented never see a flash, and the static markup carries no consent state.
 */

export function CookieBanner() {
	const {consent, acceptAll, rejectAll} = useConsent();
	const {cookieBanner, cookieBannerNotice, nav} = useMessages();
	const [mounted, setMounted] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const [showCookiePolicy, setShowCookiePolicy] = useState(false);
	const [barNode, setBarNode] = useState<HTMLDivElement | null>(null);
	// Height AND inset, read together: the reserve is the strip the bar covers, and on a phone the
	// bar docks to the viewport's edge (ui#2704), so a constant inset would reserve room it does not use.
	const [barStrip, setBarStrip] = useState({height: 0, inset: 0});
	useOverlayActivity(mounted && !consent.decided, undefined, false, false);

	useEffect(() => {
		setMounted(true);
	}, []);

	// The Bar floats fixed over the page bottom, so anything living at the very end of a page (the
	// create panel's submit button on small viewports once the Personalise row landed, ui#556) would
	// be permanently under it until the visitor answers — and the worst case has NO scroll room at
	// all: the page fits, the control sits in the bar's strip, and no gesture can free it. So the
	// banner mints that room in the ACTUAL scroll container — `[data-app-scroller]`, the Content
	// shell; the document itself never scrolls in this app (ui#758: the first version measured
	// documentElement and padded the document flow, both no-ops — dead code found by the ticket's own
	// failing precondition).
	//
	// ui#1587 changed what "mints" means, twice over. The room is now SCROLLABLE OVERFLOW rather than
	// bottom padding (see the `::after` spacer in `AppComponents`, which the two variables below
	// drive), and it is minted only for a control that would really be stranded — `consentClearance`
	// returns 0 for the page whose own content already carries its last control clear of the bar,
	// which is most of them. Before that, every page paid the bar's whole strip out of its own
	// rendered height, banner up.
	//
	// The one exemption stays contractual rather than measured: a page declaring
	// `[data-viewport-locked]` (the download page, ui#687) is pinned to the viewport and lays itself
	// out AROUND the strip, reserving the bar's room in its own bottom padding from
	// `--consent-bar-reserve` below. Since ui#2457 such a page may scroll its own content — a recipient
	// card too tall for a short window — but it scrolls INSIDE that root, and its reserve travels with
	// the content's end, so minting room out here would buy nothing and double the reservation.
	useEffect(() => {
		if (!barNode) {
			return undefined;
		}
		const scroller = document.querySelector<HTMLElement>("[data-app-scroller]");
		if (scroller === null) {
			return undefined;
		}
		const root = document.documentElement;
		const measure = () => {
			const bar = barNode.offsetHeight;
			const inset = Math.max(0, Math.round(window.innerHeight - barNode.getBoundingClientRect().bottom));
			setBarStrip({height: bar, inset});
			// Zero the spacer BEFORE reading. It is part of the scrollable overflow this reads, so a
			// live one would feed this effect's own last output back in and the value would chase
			// itself. Nothing paints between here and the writes below — one task, one layout.
			// A visitor already scrolled INTO the spacer has their offset clamped by that zeroing and
			// would be thrown back up the page when it returns, so it is put back at the end.
			//
			// The scrollbar is pinned for the same reason. Where scrollbars take space — Windows and
			// Linux, and macOS set to always show them — zeroing the spacer takes away the scrollbar
			// the clearance itself created, the scroller widens by its width, text re-wraps shorter,
			// and every control reads HIGHER than the visitor will ever see it. That shorts the
			// reservation on exactly the fitting page ui#556 is about. Reading with the scrollbar
			// present costs nothing where they overlay, and everywhere else it is the conservative
			// direction: over-reserving is capped by the rule, under-reserving is the defect.
			const scrolled = scroller.scrollTop;
			const overflowY = scroller.style.overflowY;
			scroller.style.overflowY = "scroll";
			root.style.setProperty("--consent-clearance", "0px");
			root.style.setProperty("--consent-clearance-top", "0px");
			// Forces the layout every read below then reuses. With the spacer collapsed this is the
			// page's own scrollable extent — the absolute topbar and every other out-of-flow layer
			// included, which is what the spacer has to be anchored past rather than on top of.
			const contentEnd = scroller.scrollHeight;
			const locked = scroller.querySelector("[data-viewport-locked]") !== null;
			const clearance = consentClearance({
				barHeight: bar,
				barInset: inset,
				viewportHeight: window.innerHeight,
				// Walking the page is the expensive half, and the rule answers 0 for both of these
				// without looking, so it is not walked. The rule stays the only place that decides.
				restingBottom: locked || bar <= 0
					? 0
					: lowestRestingBottom(scrollReachableControls(scroller, [barNode])),
				viewportLocked: locked,
			});
			// Anchored at the content's end, not the viewport's: on a page that already overflows,
			// a spacer at `top: 100%` would sit inside the existing overflow and buy nothing. Parked
			// back at the origin when there is nothing to mint, so a surface that reserves the bar's
			// room itself (ui#687) has no box of ours anywhere near its end.
			scroller.style.overflowY = overflowY;
			root.style.setProperty("--consent-clearance-top", clearance > 0 ? `${contentEnd}px` : "0px");
			root.style.setProperty("--consent-clearance", `${clearance}px`);
			// The strip's own size, published for surfaces that mint NO room and must instead lay
			// themselves out around the Bar (kitecorp/transferify-ui#1866: the wallpaper poster, whose
			// bottom placement puts an advertiser's only link exactly where the Bar sits). Those are
			// precisely the `[data-viewport-locked]` pages, for which `clearance` above is 0 by
			// contract — so the two variables answer two different questions and neither can stand in
			// for the other. It is the reserve rather than the raw height, so every reader is spaced
			// off the Bar by the same rule.
			root.style.setProperty("--consent-bar-reserve", `${consentBarReserve(bar, inset)}px`);
			// Restored WITHOUT reading the offset back. The three writes above have invalidated layout,
			// so `scroller.scrollTop` here would force a third full reflow per pass purely to decide
			// whether a write is needed (ui#1639). It is not needed to decide: zeroing the spacer can
			// only SHRINK the scrollable range, so it can only ever move the offset DOWN — and an
			// offset of 0, which is every reading on a freshly loaded page, cannot move at all.
			// Assigning the same value the scroller already holds is a no-op and fires no scroll event,
			// so the two branches this replaces are the same branch.
			if (scrolled > 0) {
				scroller.scrollTop = scrolled;
			}
		};
		// Coalesced to one measurement per frame: the mutation observer below fires on every DOM
		// change in the app, and each measurement forces a layout.
		let frame: number | null = null;
		let released = false;
		const scheduleMeasure = () => {
			if (frame === null && !released) {
				frame = window.requestAnimationFrame(() => {
					frame = null;
					measure();
				});
			}
		};
		const observer = new ResizeObserver(scheduleMeasure);
		observer.observe(barNode);
		observer.observe(scroller);
		// A resize observer on the scroller sees its border box, which is viewport-bound — content
		// growing INSIDE it (a route change, images landing) changes only scrollHeight. The mutation
		// observer covers that; it lives only until consent is decided, so the cost is bounded.
		// `characterData` is not optional: React updates a text-only element by writing `nodeValue` on
		// the existing node, so switching the app to Romanian — whose copy is longer, and which
		// therefore pushes the last control DOWN — mutates no child list and resizes no border box.
		const contentObserver = new MutationObserver(scheduleMeasure);
		contentObserver.observe(scroller, {childList: true, subtree: true, characterData: true});
		window.addEventListener("resize", scheduleMeasure);
		// Content that GROWS after the mount reading, without mutating the DOM, would leave that
		// reading short: webfonts swapping in and images arriving both reflow the page silently. The
		// asset `load` is captured because it fires on the image, not on the scroller. The last two
		// are asked for a different reason — a pointer going down (the first touch of a scroll gesture
		// included) and focus arriving are the moments a control has to already be reachable. All of
		// them are rare enough to re-read layout on; a scroll listener would not be.
		if (document.fonts !== undefined) {
			void document.fonts.ready.then(scheduleMeasure);
		}
		if (document.readyState !== "complete") {
			window.addEventListener("load", scheduleMeasure, {once: true});
		}
		scroller.addEventListener("load", scheduleMeasure, true);
		// A panel that opens on a height transition mutates the DOM once, at the START of the motion;
		// the geometry that matters arrives a few hundred milliseconds later with nothing to announce
		// it. These two fire at the end, once per property, and cost nothing while the page is still.
		scroller.addEventListener("transitionend", scheduleMeasure, true);
		scroller.addEventListener("animationend", scheduleMeasure, true);
		scroller.addEventListener("pointerdown", scheduleMeasure, {capture: true, passive: true});
		scroller.addEventListener("focusin", scheduleMeasure);
		measure();
		return () => {
			released = true;
			if (frame !== null) {
				window.cancelAnimationFrame(frame);
			}
			observer.disconnect();
			contentObserver.disconnect();
			window.removeEventListener("resize", scheduleMeasure);
			window.removeEventListener("load", scheduleMeasure);
			scroller.removeEventListener("load", scheduleMeasure, true);
			scroller.removeEventListener("transitionend", scheduleMeasure, true);
			scroller.removeEventListener("animationend", scheduleMeasure, true);
			scroller.removeEventListener("pointerdown", scheduleMeasure, true);
			scroller.removeEventListener("focusin", scheduleMeasure);
			root.style.removeProperty("--consent-clearance");
			root.style.removeProperty("--consent-clearance-top");
			// Removed rather than zeroed, so a reader's own `var(..., 0px)` fallback is what applies
			// once the banner is gone: one answer for "no banner yet" and "banner answered".
			root.style.removeProperty("--consent-bar-reserve");
		};
	}, [barNode]);

	// Never render into the SSG HTML, and hide once a choice has been made. The dialogs live inside
	// this early return's counterpart, so they only exist while the banner is showing (Customize /
	// the policy link are the only ways to open them from here).
	if (!mounted || consent.decided) {
		return null;
	}

	return (
		<>
			{/* The SAME reserve the clearance rule uses, not a second copy of its arithmetic: this
			    read "barHeight + 32" once, with the inset and the gap written out, and would have kept
			    its 32 the day either moved. */}
			<ScrollClearance clearance={consentBarReserve(barStrip.height, barStrip.inset)}/>
			{/* This bar renders inside the app root but floats over everything (`z-index: 1200`), so an
			    open card sheet must not read a click on it as a click on the page behind itself and
			    swallow it (ui#1247). Accept and Reject are the last controls in the product that may
			    silently do nothing. */}
			<Bar
				ref={setBarNode}
				role="region"
				aria-label={cookieBanner.regionAria}
				{...{[ABOVE_SHEET_ATTR]: ""}}
			>
				<Text>
					{cookieBannerNotice(
						<PolicyLink type="button" onClick={() => setShowCookiePolicy(true)}>
							{nav.resourcesMenu.cookie}
						</PolicyLink>,
						// The sentence NAMES this button, because pressing it also grants the email purpose
						// (ui#2596). Passed from the catalogue rather than retyped in the sentence, so the
						// quoted name and the button's label cannot drift apart.
						cookieBanner.accept,
					)}
				</Text>
				{/* Reject and Accept all are the SAME button (owner-approved, ui#2596, restoring ui#2202's
				    identical pair): same component, box, row and order, so neither answer is the easier
				    one to give. All three are outlined since owner 2026-09-23 (ui#2853); Customize is not
				    one of the two answers — it opens a dialog — so it keeps the neutral edge rather than
				    the accent, which is the only way the row distinguishes it. */}
				<Actions>
					<AnswerButton type="button" onClick={rejectAll}>{cookieBanner.reject}</AnswerButton>
					<CustomizeButton type="button" onClick={() => setShowSettings(true)}>{cookieBanner.customize}</CustomizeButton>
					<AnswerButton type="button" onClick={acceptAll}>{cookieBanner.accept}</AnswerButton>
				</Actions>
			</Bar>
			<CookieSettingsDialog open={showSettings} onClose={() => setShowSettings(false)}/>
			<PolicyDialog kind="cookie" open={showCookiePolicy} onClose={() => setShowCookiePolicy(false)}/>
		</>
	);
}
